import { FilterRail, FilterRailSkeleton } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import { PromptCard } from '@/components/prompts/PromptCard'
import type { Prompt } from '@/domain/prompt.types'
import {
	PROMPT_LIFECYCLE_LABEL,
	PROMPT_LIFECYCLE_ORDER,
	PROMPT_TYPE_LABEL,
	PROMPT_TYPE_ORDER,
} from '@/domain/prompt.types'
import { DEFAULT_PROMPT_FILTER_AXES, usePromptFilters } from './usePromptFilters'

export function PromptsPage() {
	const navigate = useNavigate()
	const f = usePromptFilters()

	const toggle = <T,>(list: T[], value: T, set: (next: T[]) => void) =>
		set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value])

	return (
		<div>
			<PageHeader
				eyebrow="Library"
				title="Prompts"
				subtitle="Versioned prompt templates with typed variables, resolved per dimension tuple."
				actions={<Button onPress={() => void navigate('/prompts/new')}>New prompt</Button>}
			/>

			<div className="grid grid-cols-1 gap-5 lg:grid-cols-[240px_1fr]">
				<aside aria-label="Filters" className="space-y-5">
					{f.axesLoading ? (
						<FilterRailSkeleton label="Loading the filters…" />
					) : (
						<FilterRail
							title="Classification"
							axes={f.axes}
							shownAxisCodes={f.shownAxisCodes}
							selection={f.selection}
							onToggle={f.toggleValue}
							onAddAxis={f.addAxis}
							onRemoveAxis={f.removeAxis}
							// The opening groups are fixed; only axes the user revealed can be removed.
							removableAxisCodes={f.shownAxisCodes.filter((c) => !DEFAULT_PROMPT_FILTER_AXES.includes(c))}
						/>
					)}

					<div>
						<h2 className="mb-2 text-ds-2xs uppercase tracking-wide text-ds-text-subtle">Type</h2>
						<ul className="space-y-1">
							{PROMPT_TYPE_ORDER.map((t) => (
								<li key={t}>
									<label className="flex items-center gap-2 text-ds-sm text-ds-text-muted">
										<input
											type="checkbox"
											checked={f.types.includes(t)}
											onChange={() => toggle(f.types, t, f.setTypes)}
										/>
										{PROMPT_TYPE_LABEL[t]}
									</label>
								</li>
							))}
						</ul>
					</div>

					<div>
						<h2 className="mb-2 text-ds-2xs uppercase tracking-wide text-ds-text-subtle">Lifecycle</h2>
						<ul className="space-y-1">
							{PROMPT_LIFECYCLE_ORDER.map((l) => (
								<li key={l}>
									<label className="flex items-center gap-2 text-ds-sm text-ds-text-muted">
										<input
											type="checkbox"
											checked={f.lifecycles.includes(l)}
											onChange={() => toggle(f.lifecycles, l, f.setLifecycles)}
										/>
										{PROMPT_LIFECYCLE_LABEL[l]}
									</label>
								</li>
							))}
						</ul>
					</div>
				</aside>

				<div>
					<input
						type="search"
						aria-label="Search prompts"
						placeholder="Search by name, code or description…"
						value={f.search}
						onChange={(e) => f.setSearch(e.target.value)}
						className="mb-4 h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface-elevated px-3 text-ds-sm text-ds-text"
					/>

					<PromptResults
						prompts={f.filtered}
						isLoading={f.isLoading}
						isError={f.isError}
						onOpen={(id) => void navigate(`/prompts/${id}`)}
					/>
				</div>
			</div>
		</div>
	)
}

function PromptResults({
	prompts,
	isLoading,
	isError,
	onOpen,
}: {
	prompts: Prompt[]
	isLoading: boolean
	isError: boolean
	onOpen: (id: string) => void
}) {
	if (isLoading) return <p className="text-ds-text-muted">Loading…</p>

	if (isError) {
		return (
			<p role="alert" className="rounded-ds-card border border-ds-danger p-8 text-center text-ds-danger">
				The prompt library could not be loaded.
			</p>
		)
	}

	if (prompts.length === 0) {
		return (
			<p className="rounded-ds-card border border-dashed border-ds-border p-8 text-center text-ds-text-muted">
				No prompts match the current filters.
			</p>
		)
	}

	return (
		<>
			<p className="mb-3 font-mono text-xs text-ds-text-subtle">
				{prompts.length} {prompts.length === 1 ? 'prompt' : 'prompts'}
			</p>
			<ul className="space-y-3">
				{prompts.map((p) => (
					<li key={p.id}>
						<PromptCard prompt={p} onOpen={() => onOpen(p.id)} />
					</li>
				))}
			</ul>
		</>
	)
}
